import React from 'react'
import { Modal } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { ordersApi } from 'api/orders'
import { EOrderPaymentMethod } from 'models/orders'
import { getOrderPath } from 'routing/utils/getOrderPath'
import { bodyScrollLock } from 'shared/utils/bodyScrollLock'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { SuperOrderSummary } from './SuperOrderSummary'
import { SuperOrderTable } from './SuperOrderTable'
import { SuperOrderTransactionsModal } from './SuperOrderTransactionsModal'
import { useSuperOrder } from './useSuperOrder'
import styles from './SuperOrderModal.module.scss'

interface ISuperOrderModalProps {
  open: boolean
  // Любой заказ из группы; null — модалку ещё ни разу не открывали
  orderId: string | null
  onClose: () => void
  // Статусы заказов на странице под модалкой после оплаты или отмены счёта устарели
  onOrdersChanged?: () => void
}

export const SuperOrderModal: React.FC<ISuperOrderModalProps> = ({
  open,
  orderId,
  onClose,
  onOrdersChanged
}) => {
  // Запрос уходит только после первого открытия, но данные переживают закрытие,
  // иначе модалка опустеет прямо во время анимации ухода
  const [wasOpened, setWasOpened] = React.useState(false)
  const [isTransactionsOpen, setIsTransactionsOpen] = React.useState(false)
  const [isInvoiceDownloading, setIsInvoiceDownloading] = React.useState(false)

  React.useEffect(() => {
    if (!open) {
      setIsTransactionsOpen(false)
      return
    }
    setWasOpened(true)
    bodyScrollLock.enable()
    return () => bodyScrollLock.disable()
  }, [open])

  const { superOrder, isLoading, isMissing, applyStatus, reload } = useSuperOrder(
    wasOpened ? orderId : null
  )

  React.useEffect(() => {
    if (open && isMissing) {
      showMessage('error', 'Супер-заказ не найден', '')
      onClose()
    }
  }, [open, isMissing])

  const downloadInvoice = async () => {
    if (!superOrder) {
      return
    }
    try {
      setIsInvoiceDownloading(true)
      const response = await ordersApi.downloadInvoice(superOrder._id)
      const blobUrl = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `Счёт № ${superOrder.number}.pdf`
      link.click()
      window.URL.revokeObjectURL(blobUrl)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsInvoiceDownloading(false)
    }
  }

  const confirmInvoicePayment = () => {
    if (!superOrder) {
      return
    }
    Modal.confirm({
      centered: true,
      title: `Оплата по счёту № ${superOrder.number} получена?`,
      content: `Сумма ${getNormalizedPrice(superOrder.sum)}. Заказы станут оплаченными и уйдут продавцам, снять отметку будет нельзя.`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Оплата получена',
      cancelText: 'Закрыть',
      async onOk() {
        try {
          await ordersApi.confirmInvoicePayment(superOrder._id)
          showMessage('success', 'Оплата по счёту отмечена', '')
          reload()
          onOrdersChanged?.()
        } catch (e) {
          showServerError(e)
        }
      }
    })
  }

  const cancelUnpaidSuperOrder = () => {
    if (!superOrder) {
      return
    }
    const orderNumbers = (superOrder.orders ?? []).map((order) => `№ ${order.number}`)
    const isInvoice = superOrder.payment_method === EOrderPaymentMethod.INVOICE
    Modal.confirm({
      centered: true,
      title: `Отменить супер-заказ № ${superOrder.number}?`,
      content: `Отменятся все заказы: ${orderNumbers.join(', ')}. Товары вернутся в продажу, покупатель получит SMS об отмене.${
        isInvoice ? ' Если деньги по счёту всё же пришли, вместо отмены отметьте оплату.' : ''
      }`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Отменить супер-заказ',
      okButtonProps: { danger: true },
      cancelText: 'Закрыть',
      async onOk() {
        try {
          await ordersApi.cancelUnpaidSuperOrder(superOrder._id)
          showMessage('success', 'Супер-заказ отменён', '')
          reload()
          onOrdersChanged?.()
        } catch (e) {
          showServerError(e)
        }
      }
    })
  }

  // Заказ открываем соседней вкладкой, чтобы не терять разобранный супер-заказ
  const openOrder = (id: string) => {
    window.open(getOrderPath(id), '_blank', 'noopener,noreferrer')
  }

  return (
    <>
      <Modal
        centered
        open={open}
        onCancel={onClose}
        footer={null}
        title={
          superOrder ? `Супер-заказ № ${superOrder.number}` : 'Супер-заказ'
        }
        wrapClassName={styles.wrapper}
        className={styles.modal}
      >
        {superOrder && (
          <SuperOrderSummary
            superOrder={superOrder}
            onOpenTransactions={() => setIsTransactionsOpen(true)}
            onDownloadInvoice={downloadInvoice}
            isInvoiceDownloading={isInvoiceDownloading}
            onConfirmInvoicePayment={confirmInvoicePayment}
            onCancelUnpaid={cancelUnpaidSuperOrder}
          />
        )}
        <SuperOrderTable
          orders={superOrder?.orders}
          isLoading={isLoading}
          onOpenOrder={openOrder}
          onStatusChange={applyStatus}
        />
      </Modal>
      <SuperOrderTransactionsModal
        open={open && isTransactionsOpen}
        superOrder={superOrder}
        onClose={() => setIsTransactionsOpen(false)}
      />
    </>
  )
}
